'use client'

import { Suspense, lazy, useCallback, useEffect, useState } from 'react'
import { Star } from 'lucide-react'
import { toast } from 'sonner'
import type { FanfareState } from './mission-fanfare-ui'

const MissionFanfareUI = lazy(() => import('./mission-fanfare-ui'))

/**
 * Lightweight wrapper that listens for mission events and only pulls in the
 * heavy framer-motion-backed UI (and its chunk) when a fanfare actually fires.
 * Keeps framer-motion out of the initial paint for users who never trigger one.
 */
export function MissionFanfare() {
  const [activeFanfare, setActiveFanfare] = useState<FanfareState | null>(null)
  const [uiPreloaded, setUiPreloaded] = useState(false)

  const triggerFanfare = useCallback(async (title: string, xp: number) => {
    const id = Math.random().toString(36).substring(2, 9)
    setActiveFanfare({ id, title, xp })
    setUiPreloaded(true)

    setTimeout(() => {
      setActiveFanfare((prev) => (prev?.id === id ? null : prev))
    }, 5000)
  }, [])

  useEffect(() => {
    const handleMissionClaimed = (e: Event) => {
      const detail = (e as CustomEvent<{ title?: string; xpAwarded?: number }>).detail || {}
      if (detail.title) {
        void triggerFanfare(detail.title, detail.xpAwarded || 0)
      }
    }

    const handleMissionProgressed = (e: Event) => {
      const detail = (e as CustomEvent<{ title?: string; currentValue?: number; targetValue?: number }>).detail || {}
      const { title, currentValue = 0, targetValue = 0 } = detail
      if (!title) return

      const progress = targetValue > 0 ? Math.min(currentValue / targetValue, 1) : 0
      const percent = Math.round(progress * 100)

      toast.custom(
        () => (
          <div className="flex w-full items-center gap-3 rounded-lg border border-primary/20 bg-card p-3 shadow-lg">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Star className="size-5 fill-primary" />
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-xs font-black uppercase tracking-tight text-muted-foreground">Progresso em Missao</p>
              <p className="truncate text-sm font-bold text-foreground">{title}</p>
              <div className="mt-1.5 flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-transform duration-500 ease-out"
                    style={{ width: '100%', transform: `scaleX(${progress})`, transformOrigin: '0 0' }}
                  />
                </div>
                <span className="whitespace-nowrap text-xs font-black text-primary">
                  {currentValue}/{targetValue}
                </span>
              </div>
              <span className="sr-only">{percent}% concluído</span>
            </div>
          </div>
        ),
        {
          duration: 3000,
          position: 'bottom-center',
        },
      )
    }

    const handleSyncStatus = () => {
      // Safety net: mission-claimed/progressed events should already have fired
      // via syncAutoMissionClaims. Listener kept intentionally for diagnostics.
    }

    window.addEventListener('habitpack:mission-claimed', handleMissionClaimed)
    window.addEventListener('habitpack:mission-progressed', handleMissionProgressed)
    window.addEventListener('habitpack:sync-status', handleSyncStatus)

    return () => {
      window.removeEventListener('habitpack:mission-claimed', handleMissionClaimed)
      window.removeEventListener('habitpack:mission-progressed', handleMissionProgressed)
      window.removeEventListener('habitpack:sync-status', handleSyncStatus)
    }
  }, [triggerFanfare])

  if (!uiPreloaded) return null

  return (
    <Suspense fallback={null}>
      <MissionFanfareUI fanfare={activeFanfare} onClose={() => setActiveFanfare(null)} />
    </Suspense>
  )
}
